import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { activeCurrencySymbol } from "../../utils/currency";

interface DataPoint {
  label: string;
  revenue: number;
}

interface RevenueData {
  today: DataPoint[];
  thisWeek: DataPoint[];
  thisMonth: DataPoint[];
}

interface RevenueChartProps {
  revenueData: RevenueData;
  totalRevenue?: number;
}

type TimeRange = "today" | "this_week" | "this_month";

const CustomCursor = (props: any) => {
  const { points, height } = props;
  if (!points || points.length === 0) return null;
  const x = points[0].x;
  const barWidth = 28;
  const left = x - barWidth / 2;
  const top = -8;
  const barHeight = (height ?? 0) + 16;

  return (
    <g pointerEvents="none">
      <defs>
        <linearGradient id="cursorGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bbbbbb" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#bbbbbb" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      <rect
        x={left}
        y={top}
        width={barWidth}
        height={barHeight}
        rx={8}
        ry={8}
        fill="url(#cursorGrad)"
        stroke="none"
        tabIndex={-1}
        focusable="false"
      />
    </g>
  );
};

const CustomTooltip = (props: TooltipProps<number, string>) => {
  const { active, payload } = props as any;
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          borderRadius: 20,
          background: "#2563eb",
          padding: "6px 14px",
          fontSize: 14,
          fontWeight: 600,
          color: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          pointerEvents: "none",
        }}
      >
        {activeCurrencySymbol()}{payload[0].value.toFixed(2)}
      </div>
    );
  }
  return null;
};

const ActiveDot = (props: any) => {
  const { cx, cy } = props;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={6.5}
      fill="#1a1a1a"
      stroke="#f8f8f8"
      strokeWidth={2.5}
    />
  );
};

const formatAxisTick = (value: number) => {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2).replace(/\.?0+$/, "");
};

export default function RevenueChart({
  revenueData,
  totalRevenue,
}: RevenueChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("this_month");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const timeRangeLabels: Record<TimeRange, string> = {
    today: "Today",
    this_week: "This Week",
    this_month: "This Month",
  };

  const getCurrentData = (): DataPoint[] => {
    switch (timeRange) {
      case "today":
        return revenueData.today;
      case "this_week":
        return revenueData.thisWeek;
      case "this_month":
        return revenueData.thisMonth;
      default:
        return revenueData.thisMonth;
    }
  };

  const chartData = getCurrentData();
  const displayedRevenue = chartData.reduce((sum, item) => sum + item.revenue, 0);
  const maxRevenue = Math.max(...chartData.map((item) => item.revenue), 0);
  const yMax = maxRevenue > 0 ? Math.ceil(maxRevenue * 1.15) : 10;
  const yTicks = [0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax].map((v) =>
    Number(v.toFixed(2)),
  );

  // For today view, show fewer labels to avoid crowding
  const getTickInterval = () => {
    if (timeRange === "today") return 3; // Show every 4th hour
    if (timeRange === "this_month" && chartData.length > 15) return 2; // Show every 3rd day
    return 0; // Show all
  };

  const ranges: TimeRange[] = ["today", "this_week", "this_month"];

  return (
    <div className="revenue-chart border h-full w-full rounded-xl bg-white p-3 sm:p-4 md:p-5 shadow-[0_2px_24px_rgba(0,0,0,0.07)] [&_.recharts-surface:focus]:outline-none [&_.recharts-surface:focus-visible]:outline-none [&_.recharts-wrapper:focus]:outline-none">
      <style>
        {`
          .revenue-chart *:focus,
          .revenue-chart *:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
          .revenue-chart .recharts-tooltip-cursor {
            stroke: none !important;
          }
        `}
      </style>
      {/* Header */}
      <div className="mb-6 flex flex-row gap-3 sm:mb-8 items-center justify-between">
        <div>
          <div className="text-[12px] font-medium text-[#888]">
            Total Revenue ({timeRangeLabels[timeRange]})
          </div>
          <div className="text-[20px] font-bold tracking-[-0.5px] text-blue-600 sm:text-[22px]">
            {activeCurrencySymbol()}{displayedRevenue.toFixed(2)}
          </div>
        </div>

        {/* Dropdown */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex cursor-pointer items-center gap-2 rounded-[10px] border-[1.5px] border-[#e5e5e5] bg-white px-3 py-2 text-xs font-medium text-[#333] outline-none sm:px-4 sm:text-[13px]"
          >
            {timeRangeLabels[timeRange]}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 4l4 4 4-4"
                stroke="#666"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 top-[calc(100%+6px)] z-10 min-w-[160px] overflow-hidden rounded-[10px] border-[1.5px] border-[#e5e5e5] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.1)]">
              {ranges.map((r) => (
                <div
                  key={r}
                  onClick={() => {
                    setTimeRange(r);
                    setDropdownOpen(false);
                  }}
                  className={`cursor-pointer px-3 py-2 text-xs text-[#333] transition-colors hover:bg-[#f9f9f9] sm:px-4 sm:py-[9px] sm:text-[13px] ${
                    timeRange === r
                      ? "bg-[#f5f5f5] font-semibold"
                      : "bg-white font-normal"
                  }`}
                >
                  {timeRangeLabels[r]}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="h-[220px] sm:h-[250px] md:h-[280px]">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              accessibilityLayer={false}
              tabIndex={-1}
              className="outline-none focus:outline-none focus-visible:outline-none"
              data={chartData}
              margin={{ top: 8, right: 8, left: -6, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#d0d0d0"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                padding={{ left: 0, right: 0 }}
                tick={{
                  fontSize: 11,
                  fill: "#aaa",
                  fontFamily: "'DM Sans', sans-serif",
                }}
                dy={8}
                interval={getTickInterval()}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={58}
                tickMargin={3}
                tick={{
                  fontSize: 11,
                  fill: "#aaa",
                  fontFamily: "'DM Sans', sans-serif",
                }}
                domain={[0, yMax]}
                ticks={yTicks}
                tickFormatter={formatAxisTick}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={<CustomCursor />}
                position={{ y: -10 }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={false}
                activeDot={<ActiveDot />}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            No revenue data for {timeRangeLabels[timeRange].toLowerCase()}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-2 flex justify-end gap-6">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded-[3px] bg-blue-600" />
          <span className="text-xs font-medium text-[#555] sm:text-[13px]">
            Revenue
          </span>
        </div>
      </div>
    </div>
  );
}
